import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const VolumeCalculator = () => {
  const [inputData, setInputData] = useState({
    shape: 'cube',
    length: '',
    width: '',
    height: '',
    radius: '',
    result: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateVolume = () => {
    const { shape, length, width, height, radius } = inputData;
    
    // Validate inputs based on selected shape
    if (!length) {
      setError('Please enter a valid length');
      return;
    }

    const l = parseFloat(length);
    if (isNaN(l) || l <= 0) {
      setError('Length must be a positive number');
      return;
    }

    let volume = 0;
    let formula = '';
    let calculationSteps = [];

    switch (shape) {
      case 'cube':
        if (!width || !height) {
          setError('Please enter width and height for cube');
          return;
        }
        const w = parseFloat(width);
        const h = parseFloat(height);
        if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) {
          setError('Width and height must be positive numbers');
          return;
        }
        volume = l * w * h;
        formula = 'V = length × width × height';
        calculationSteps = [
          `Step 1: Use the formula for cube volume: ${formula}`,
          `Step 2: Substitute the values: V = ${l} × ${w} × ${h}`,
          `Step 3: Calculate the result: V = ${volume.toFixed(2)}`
        ];
        break;

      case 'sphere':
        if (!radius) {
          setError('Please enter a radius for sphere');
          return;
        }
        const r = parseFloat(radius);
        if (isNaN(r) || r <= 0) {
          setError('Radius must be a positive number');
          return;
        }
        volume = (4/3) * Math.PI * Math.pow(r, 3);
        formula = 'V = (4/3) × π × r³';
        calculationSteps = [
          `Step 1: Use the formula for sphere volume: ${formula}`,
          `Step 2: Substitute the values: V = (4/3) × π × ${r}³`,
          `Step 3: Calculate the result: V = ${volume.toFixed(2)}`
        ];
        break;

      case 'cylinder':
        if (!radius || !height) {
          setError('Please enter radius and height for cylinder');
          return;
        }
        const rCyl = parseFloat(radius);
        const hCyl = parseFloat(height);
        if (isNaN(rCyl) || isNaN(hCyl) || rCyl <= 0 || hCyl <= 0) {
          setError('Radius and height must be positive numbers');
          return;
        }
        volume = Math.PI * Math.pow(rCyl, 2) * hCyl;
        formula = 'V = π × r² × h';
        calculationSteps = [
          `Step 1: Use the formula for cylinder volume: ${formula}`,
          `Step 2: Substitute the values: V = π × ${rCyl}² × ${hCyl}`,
          `Step 3: Calculate the result: V = ${volume.toFixed(2)}`
        ];
        break;

      case 'cone':
        if (!radius || !height) {
          setError('Please enter radius and height for cone');
          return;
        }
        const rCone = parseFloat(radius);
        const hCone = parseFloat(height);
        if (isNaN(rCone) || isNaN(hCone) || rCone <= 0 || hCone <= 0) {
          setError('Radius and height must be positive numbers');
          return;
        }
        volume = (1/3) * Math.PI * Math.pow(rCone, 2) * hCone;
        formula = 'V = (1/3) × π × r² × h';
        calculationSteps = [
          `Step 1: Use the formula for cone volume: ${formula}`,
          `Step 2: Substitute the values: V = (1/3) × π × ${rCone}² × ${hCone}`,
          `Step 3: Calculate the result: V = ${volume.toFixed(2)}`
        ];
        break;

      case 'pyramid':
        if (!length || !width || !height) {
          setError('Please enter length, width, and height for pyramid');
          return;
        }
        const lPyra = parseFloat(length);
        const wPyra = parseFloat(width);
        const hPyra = parseFloat(height);
        if (isNaN(lPyra) || isNaN(wPyra) || isNaN(hPyra) || lPyra <= 0 || wPyra <= 0 || hPyra <= 0) {
          setError('All dimensions must be positive numbers');
          return;
        }
        volume = (lPyra * wPyra * hPyra) / 3;
        formula = 'V = (length × width × height) / 3';
        calculationSteps = [
          `Step 1: Use the formula for pyramid volume: ${formula}`,
          `Step 2: Substitute the values: V = (${lPyra} × ${wPyra} × ${hPyra}) / 3`,
          `Step 3: Calculate the result: V = ${volume.toFixed(2)}`
        ];
        break;

      default:
        setError('Please select a valid shape');
        return;
    }

    // Create visualization data
    const chartData = {
      labels: ['Volume'],
      datasets: [{
        label: 'Volume Calculation',
        data: [volume],
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }]
    };

    setResults({
      shape,
      formula,
      volume: volume.toFixed(2),
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    shape: 'cube',
    length: 3,
    width: 3,
    height: 3,
    volume: 27
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-blue-100 to-indigo-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Volume Calculator | Geometric Shape Volume Calculator</title>
          <meta name="description" content="Calculate the volume of various geometric shapes including cube, sphere, cylinder, cone, and pyramid. This calculator provides accurate volume measurements for different 3D shapes." />
          <meta name="keywords" content="volume calculator, geometric shapes, cube volume, sphere volume, cylinder volume, cone volume, pyramid volume, math calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-blue-600 mr-4">📏</div>
            <h1 className="text-3xl font-bold text-gray-800">Volume Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the volume of various geometric shapes including cube, sphere, cylinder, cone, and pyramid. This calculator provides accurate volume measurements for different 3D shapes.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Shape:</label>
              <select
                name="shape"
                value={inputData.shape}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="cube">Cube</option>
                <option value="sphere">Sphere</option>
                <option value="cylinder">Cylinder</option>
                <option value="cone">Cone</option>
                <option value="pyramid">Pyramid</option>
              </select>
            </div>

            {inputData.shape === 'cube' && (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Length:</label>
                  <input
                    type="number"
                    name="length"
                    value={inputData.length}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter length"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Width:</label>
                  <input
                    type="number"
                    name="width"
                    value={inputData.width}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter width"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height:</label>
                  <input
                    type="number"
                    name="height"
                    value={inputData.height}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter height"
                  />
                </div>
              </>
            )}

            {inputData.shape === 'sphere' && (
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Radius:</label>
                <input
                  type="number"
                  name="radius"
                  value={inputData.radius}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter radius"
                />
              </div>
            )}

            {(inputData.shape === 'cylinder' || inputData.shape === 'cone') && (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Radius:</label>
                  <input
                    type="number"
                    name="radius"
                    value={inputData.radius}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter radius"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height:</label>
                  <input
                    type="number"
                    name="height"
                    value={inputData.height}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter height"
                  />
                </div>
              </>
            )}

            {inputData.shape === 'pyramid' && (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Length:</label>
                  <input
                    type="number"
                    name="length"
                    value={inputData.length}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter length"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Width:</label>
                  <input
                    type="number"
                    name="width"
                    value={inputData.width}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter width"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height:</label>
                  <input
                    type="number"
                    name="height"
                    value={inputData.height}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter height"
                  />
                </div>
              </>
            )}
          </div>

          <button
            onClick={calculateVolume}
            className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Volume
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Volume Calculation</h3>
                  <p className="text-lg">Shape: {results.shape.charAt(0).toUpperCase() + results.shape.slice(1)}</p>
                  <p className="text-lg">Formula: {results.formula}</p>
                  <p className="text-2xl font-bold text-blue-600 mt-2">{results.volume}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Shape Visualization</h3>
                  <div className="h-48 bg-white rounded-lg flex items-center justify-center">
                    <svg width="200" height="200" viewBox="0 0 200 200">
                      {results.shape === 'cube' && (
                        <g>
                          <rect x="50" y="50" width="100" height="100" stroke="blue" strokeWidth="2" fill="none" />
                          <rect x="80" y="20" width="100" height="100" stroke="blue" strokeWidth="2" fill="none" transform="rotate(45, 150, 50)" />
                          <rect x="80" y="20" width="100" height="100" stroke="blue" strokeWidth="2" fill="none" transform="rotate(-45, 150, 150)" />
                        </g>
                      )}
                      {results.shape === 'sphere' && (
                        <circle cx="100" cy="100" r="50" stroke="blue" strokeWidth="2" fill="none" />
                      )}
                      {results.shape === 'cylinder' && (
                        <g>
                          <rect x="75" y="50" width="50" height="100" rx="25" ry="25" stroke="blue" strokeWidth="2" fill="none" />
                          <rect x="75" y="50" width="50" height="100" stroke="blue" strokeWidth="2" fill="none" />
                        </g>
                      )}
                      {results.shape === 'cone' && (
                        <g>
                          <ellipse cx="100" cy="50" rx="30" ry="10" stroke="blue" strokeWidth="2" fill="none" />
                          <line x1="70" y1="50" x2="100" y2="150" stroke="blue" strokeWidth="2" />
                          <line x1="130" y1="50" x2="100" y2="150" stroke="blue" strokeWidth="2" />
                        </g>
                      )}
                      {results.shape === 'pyramid' && (
                        <g>
                          <polygon points="50,150 150,150 100,50" stroke="blue" strokeWidth="2" fill="none" />
                          <line x1="50" y1="150" x2="150" y2="150" stroke="blue" strokeWidth="2" />
                        </g>
                      )}
                    </svg>
                  </div>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Volume'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Result'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Volume Calculation'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for a cube:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Shape: Cube</li>
                <li>Length: {exampleData.length}</li>
                <li>Width: {exampleData.width}</li>
                <li>Height: {exampleData.height}</li>
                <li>Volume: {exampleData.volume}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Use the formula for cube volume: V = length × width × height</li>
                <li>Substitute the values: V = 3 × 3 × 3</li>
                <li>Calculate the result: V = 27</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Volume Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Select Shape:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose from cube, sphere, cylinder, cone, or pyramid</li>
                </ul>
              </li>
              <li><strong>Enter Dimensions:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Provide the required dimensions based on the selected shape</li>
                  <li>Ensure all values are positive numbers</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The volume of the selected shape</li>
                  <li>A visual representation of the shape</li>
                  <li>Detailed calculation steps</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Use consistent units for all dimensions (e.g., all in meters)</li>
                <li>For complex shapes, break them into simpler shapes and calculate separately</li>
                <li>Double-check your inputs for accuracy</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Volume Calculations <span className="text-blue-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Volume is the amount of space occupied by a three-dimensional object. Here's what each shape represents:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Cube:</strong> A three-dimensional shape with six square faces</li>
              <li><strong>Sphere:</strong> A perfectly round geometrical object in three-dimensional space</li>
              <li><strong>Cylinder:</strong> A solid geometric figure with straight parallel sides and a circular or oval cross-section</li>
              <li><strong>Cone:</strong> A three-dimensional geometric shape that tapers smoothly from a flat base to a point called the apex or vertex</li>
              <li><strong>Pyramid:</strong> A structure whose outer surfaces are triangular and converge to a single step at the top</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Unit Consistency:</strong> Always use the same units for all dimensions</li>
              <li><strong>Double-check Inputs:</strong> Verify all measurements before calculating</li>
              <li><strong>Real-world Applications:</strong> Use for packaging, construction, and engineering projects</li>
              <li><strong>Complex Shapes:</strong> Break down complex shapes into simpler ones for easier calculation</li>
            </ul>
            <p className="italic mt-4">
              Note: For precise engineering applications, consult with a professional for critical calculations.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default VolumeCalculator;